import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Button, TextField, Grid, InputAdornment, IconButton, CircularProgress, useTheme } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TabelaArquivos from './components/TabelaArquivos';
import { IBackupArquivo } from './types';
import { CentralBackupsApi } from './services/centralBackupsApi';

interface ArquivosDaTarefaProps {
  tarefaAgendadaID: string;
  nomeTarefa: string;
  token: string;
  onVoltar: () => void;
  onBaixar: (arquivoID: string, nome: string) => void;
  baixandoArquivoID: string | null;
}

export const ArquivosDaTarefa: React.FC<ArquivosDaTarefaProps> = ({
  tarefaAgendadaID,
  nomeTarefa,
  token,
  onVoltar,
  onBaixar,
  baixandoArquivoID
}) => {
  const theme = useTheme();
  const [arquivos, setArquivos] = useState<IBackupArquivo[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // Paginação e Ordenação
  const [pagina, setPagina] = useState(0);
  const [linhasPorPagina, setLinhasPorPagina] = useState(10);
  const ordenarPor = 'dataCadastro';
  const direcao = 'DESC';

  // Filtros
  const [filtroBusca, setFiltroBusca] = useState('');
  const [buscaAtiva, setBuscaAtiva] = useState('');
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');

  const carregarArquivos = useCallback(() => {
    setCarregando(true);
    setErro(null);

    const offset = pagina * linhasPorPagina;

    // Converter datas YYYY-MM-DD para ISO no início/fim do dia
    const dataInicialISO = dataInicial ? `${dataInicial}T00:00:00.000Z` : undefined;
    const dataFinalISO = dataFinal ? `${dataFinal}T23:59:59.999Z` : undefined;

    CentralBackupsApi.obterArquivosTarefa(tarefaAgendadaID, token, {
      limit: linhasPorPagina,
      offset,
      q: buscaAtiva || undefined,
      dataInicial: dataInicialISO,
      dataFinal: dataFinalISO,
      ordenarPor,
      direcao
    })
      .then((data) => {
        setArquivos(data.items);
        setTotal(data.total);
        setCarregando(false);
      })
      .catch((err) => {
        console.error("Erro ao carregar arquivos da tarefa", err);
        setErro("Não foi possível carregar os arquivos dessa tarefa.");
        setCarregando(false);
      });
  }, [tarefaAgendadaID, token, pagina, linhasPorPagina, buscaAtiva, dataInicial, dataFinal]);

  useEffect(() => {
    carregarArquivos();
  }, [carregarArquivos]);

  const lidarComBusca = (e: React.FormEvent) => {
    e.preventDefault();
    setPagina(0);
    setBuscaAtiva(filtroBusca);
  };

  const limparFiltros = () => {
    setFiltroBusca('');
    setBuscaAtiva('');
    setDataInicial('');
    setDataFinal('');
    setPagina(0);
  };

  return (
    <Box>
      {/* Cabeçalho com botão voltar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={onVoltar} sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', backgroundColor: 'rgba(148, 163, 184, 0.1)' } }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Arquivos da Tarefa
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Visualizando arquivos gerados por: <strong style={{ color: theme.palette.primary.main }}>{nomeTarefa}</strong>
          </Typography>
        </Box>
      </Box>

      {/* Filtros */}
      <Box
        component="form"
        onSubmit={lidarComBusca}
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? '#0F172A' : 'rgba(0, 0, 0, 0.02)',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
          p: 2.5,
          mb: 4
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              placeholder="Pesquisar por nome do arquivo..."
              value={filtroBusca}
              onChange={(e) => setFiltroBusca(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.8)' }
              }}
            />
          </Grid>

          <Grid item xs={6} md={2.5}>
            <TextField
              type="date"
              label="Data Inicial"
              value={dataInicial}
              onChange={(e) => {
                setDataInicial(e.target.value);
                setPagina(0);
              }}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.8)' } }}
            />
          </Grid>

          <Grid item xs={6} md={2.5}>
            <TextField
              type="date"
              label="Data Final"
              value={dataFinal}
              onChange={(e) => {
                setDataFinal(e.target.value);
                setPagina(0);
              }}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.8)' } }}
            />
          </Grid>

          <Grid item xs={12} md={2} sx={{ display: 'flex', gap: 1 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ textTransform: 'none', height: '40px' }}
            >
              Filtrar
            </Button>
            <IconButton
              onClick={limparFiltros}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                color: 'text.secondary',
                width: '40px',
                height: '40px',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(42, 81, 247, 0.04)',
                  color: 'primary.main'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      {/* Erro ou Tabela */}
      {erro && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body1" color="error" sx={{ mb: 2 }}>
            {erro}
          </Typography>
          <Button variant="outlined" onClick={carregarArquivos}>
            Tentar Novamente
          </Button>
        </Box>
      )}

      {carregando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        !erro && (
          <TabelaArquivos
            arquivos={arquivos}
            total={total}
            pagina={pagina}
            linhasPorPagina={linhasPorPagina}
            onMudarPagina={(novaPagina) => setPagina(novaPagina)}
            onMudarLinhasPorPagina={(novasLinhas) => {
              setLinhasPorPagina(novasLinhas);
              setPagina(0);
            }}
            onBaixar={onBaixar}
            baixandoArquivoID={baixandoArquivoID}
            token={token}
          />
        )
      )}
    </Box>
  );
};

export default ArquivosDaTarefa;
